import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { Skeleton } from '../../../../components/Skeleton';
import { ApiError } from '../../../../lib/apiError';
import { casesApi } from '../../../case-management/cases/casesApi';
import { CaseFilesPanel } from '../../../case-management/cases/components/CaseFilesPanel';
import { postOperationReportsApi } from '../../../case-management/cases/postOperationReportsApi';
import { REPORT_FILE_MAX_BYTES, type CaseListItem, type PostOperationReportListItem } from '../../../case-management/cases/caseTypes';

/** Why the files cannot be changed from HERE — a press release shows a case's reports, it does not file into them. */
const READ_HERE = 'Files are attached on the report itself — open it from its case.';

/**
 * The referenced case's Post Operation Reports, on the press release that cites the case.
 *
 * **Read through the CASE's own endpoints, as the reader** — the release carries only the case's
 * id. Membership, the access level and the `post-operation-reports.*` keys all apply exactly as
 * they do on the case, so a release reader who could not open the case is told so and shown
 * nothing of it: not its number, not its title, not a report.
 *
 * Each report is its summary and its files, the files through Case Management's own panel so
 * download and its key cannot disagree with the report's page; changing them stays there.
 */
export function PostOperationReportsPanel({ caseId }: { caseId: string }) {
  const canReadCase = usePermission('cases.read');

  const [reports, setReports] = useState<PostOperationReportListItem[] | null>(null);
  const [caseRecord, setCaseRecord] = useState<CaseListItem | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      postOperationReportsApi
        .list(caseId, { page: 1, pageSize: 50, status: 'active' }, signal)
        .then((page) => {
          if (signal?.aborted) return;
          setReports(page.items);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load the reports.', 0));
        });

      // The title is the case's — read only by a reader holding the case's own key.
      if (canReadCase) {
        casesApi
          .get(caseId, signal)
          .then((found) => {
            if (!signal?.aborted) setCaseRecord(found);
          })
          // The reports' own refusal says what the reader needs to know; this one adds nothing.
          .catch(() => undefined);
      }
    },
    [caseId, canReadCase],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  if (error && reports === null) {
    // 404 and 403 are the case's own answers: not a member, or above the reader's level.
    return error.status === 404 || error.status === 403 ? (
      <EmptyState
        message={
          error.status === 403
            ? 'This press release references a case above your access level, so its Post Operation Reports are not shown.'
            : "This press release references a case you are not a member of, so its Post Operation Reports are not shown. A member of the case can share it with you."
        }
      />
    ) : (
      <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
    );
  }

  if (reports === null) {
    return (
      <ProfileCard id="press-release-profile-reports-loading" title="Post Operation Report">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-5 w-1/2" />
        </div>
      </ProfileCard>
    );
  }

  const caseNumber = caseRecord?.caseNumber ?? reports[0]?.caseNumber ?? null;

  return (
    <>
      <ProfileCard id="press-release-profile-case" title="Case">
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Case number', value: caseNumber, mono: true },
            ...(canReadCase ? [{ label: 'Title', value: caseRecord?.title ?? null }] : []),
          ]}
        />
      </ProfileCard>

      {reports.length === 0 ? (
        <EmptyState message="No Post Operation Report has been written for this case yet." />
      ) : (
        reports.map((report) => <ReportCard key={report.id} report={report} />)
      )}
    </>
  );
}

function ReportCard({ report }: { report: PostOperationReportListItem }) {
  const navigate = useNavigate();
  const files = useMemo(() => postOperationReportsApi.files(report.id), [report.id]);

  return (
    <ProfileCard
      id={`press-release-profile-report-${report.id}`}
      title={report.title}
      count={report.attachmentCount}
      menuItems={[
        {
          key: 'open',
          label: 'Open report',
          // The report's own page, under its case — where its summary and files are changed.
          onClick: () => navigate(`/cases/${report.caseId}/reports/${report.id}`),
        },
      ]}
    >
      <div className="flex flex-col gap-4">
        {/* Only the prose is inset — the files table takes the card's full width, or its columns clip. */}
        <p className="whitespace-pre-wrap px-5 pt-4 text-sm text-slate-600">{report.summary}</p>

        <div className="flex h-[360px] min-h-0 min-w-0 flex-col">
          <CaseFilesPanel
            id={`press-release-report-files-${report.id}`}
            files={files}
            maxBytes={REPORT_FILE_MAX_BYTES}
            recordLabel="this report"
            attachPermission="post-operation-reports.attach"
            downloadPermission="post-operation-reports.download"
            writeReason={READ_HERE}
          />
        </div>
      </div>
    </ProfileCard>
  );
}
